<template>
    <div class="otp-input-container">
        <div class="flex items-center gap-2 w-full">
            <template v-for="(digit, index) in digits" :key="index">
                <input
                    :ref="el => { if (el) inputRefs[index] = el }"
                    v-model="digits[index]"
                    type="text"
                    inputmode="text"
                    :autocomplete="index === 0 ? 'one-time-code' : 'off'"
                    autocapitalize="characters"
                    autocorrect="off"
                    spellcheck="false"
                    :class="getInputClasses"
                    :aria-label="`OTP digit ${index + 1}`"
                    @input="handleInput(index, $event)"
                    @keydown="handleKeydown(index, $event)"
                    @paste="handlePaste($event)"
                    @focus="handleFocus(index)"
                />
                <span 
                    v-if="index < length - 1" 
                    class="text-gray-400 text-lg font-semibold flex-shrink-0"
                    aria-hidden="true"
                >
                    •
                </span>
            </template>
        </div>
        <!-- Hidden input for form submission -->
        <input
            type="hidden"
            :name="name"
            :id="id"
            :value="otpValue"
        />
    </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue';

const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    name: {
        type: String,
        default: 'token'
    },
    id: {
        type: String,
        default: 'token'
    },
    length: {
        type: Number,
        default: 5
    },
    inputClasses: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['update:modelValue', 'enter']);

const digits = ref(Array(props.length).fill(''));
const inputRefs = ref([]);

const otpValue = computed(() => {
    return digits.value.join('');
});

watch(() => props.modelValue, (newValue) => {
    if (newValue && newValue.length === props.length) {
        digits.value = newValue.split('');
    } else if (!newValue) {
        digits.value = Array(props.length).fill('');
    }
}, { immediate: true });

watch(otpValue, (newValue) => {
    emit('update:modelValue', newValue);
});

const getInputClasses = computed(() => {
    // Parse inputClasses string to extract styling from brand settings
    const inputClassesArray = props.inputClasses ? props.inputClasses.split(' ') : [];
    
    // Extract relevant classes from the email input styling
    const baseClasses = [
        'flex-1', // Equal width for each input
        'text-center text-lg font-semibold',
        'transition-shadow',
        'focus:outline-none'
    ];
    
    // Extract background, text, ring, and border classes from inputClasses
    const bgClasses = inputClassesArray.filter(cls => cls.startsWith('bg-'));
    const textClasses = inputClassesArray.filter(cls => cls.startsWith('text-'));
    const ringClasses = inputClassesArray.filter(cls => 
        cls.startsWith('ring-') || 
        cls.startsWith('focus:ring-')
    );
    // Border classes (like border-0) should be included to match email input
    const borderClasses = inputClassesArray.filter(cls => cls.startsWith('border-'));
    
    // Extract corner radius classes (rounded-xl, rounded-lg, etc.)
    const cornerClasses = inputClassesArray.filter(cls => 
        cls.startsWith('rounded-') || cls === 'rounded'
    );
    
    // Extract padding classes - keep horizontal padding, adjust vertical for OTP
    const paddingClasses = inputClassesArray.filter(cls => 
        cls.startsWith('px-') || cls.startsWith('py-')
    );
    
    // Use appropriate padding for OTP inputs
    const adjustedPadding = paddingClasses.map(cls => {
        if (cls.startsWith('py-')) {
            // Keep vertical padding but make it slightly smaller for better OTP appearance
            return 'py-3'; // Slightly smaller than email input's py-3.5
        }
        // Keep horizontal padding as-is
        return cls;
    });
    
    // Combine all classes
    return [
        ...baseClasses,
        ...bgClasses,
        ...textClasses,
        ...borderClasses, // Include border classes (e.g., border-0)
        ...ringClasses, // Include ring classes (e.g., ring-1 ring-inset)
        ...adjustedPadding,
        ...(cornerClasses.length > 0 ? cornerClasses : ['rounded-lg']), // Default to rounded-lg if no corners specified
        'min-w-0' // Prevent flex items from overflowing
    ].join(' ');
});

// Distribute a multi-character string across all boxes, starting from the first.
// Used for iOS "one-time-code" autofill, paste, and fast multi-char inserts.
const fillFromStart = (raw) => {
    const sanitized = (raw || '').replace(/[^0-9a-zA-Z]/g, '').toUpperCase().slice(0, props.length);

    if (!sanitized) {
        return;
    }

    for (let i = 0; i < props.length; i++) {
        digits.value[i] = sanitized[i] || '';
    }

    // Focus the last filled input (or the last box when fully filled)
    const lastFilledIndex = Math.min(sanitized.length, props.length) - 1;
    nextTick(() => {
        // Force each DOM input to match state. Without this, if a box's value is
        // unchanged (e.g. re-autofill where the first char matches), Vue skips the
        // reconcile and the raw multi-char autofill string lingers in that box.
        for (let i = 0; i < props.length; i++) {
            if (inputRefs.value[i]) {
                inputRefs.value[i].value = digits.value[i];
            }
        }
        inputRefs.value[lastFilledIndex]?.focus();
    });
};

const handleInput = (index, event) => {
    const raw = event.target.value;

    // Multi-character insert (iOS one-time-code autofill, paste, or fast typing):
    // spread the characters across the boxes instead of dropping them.
    if (raw.replace(/[^0-9a-zA-Z]/g, '').length > 1) {
        fillFromStart(raw);
        return;
    }

    const value = raw.replace(/[^0-9a-zA-Z]/g, '').slice(0, 1).toUpperCase();
    digits.value[index] = value;

    // Keep the DOM value in sync when an invalid character was stripped
    if (event.target.value !== value) {
        event.target.value = value;
    }

    // Move to next input if value entered
    if (value && index < props.length - 1) {
        nextTick(() => {
            inputRefs.value[index + 1]?.focus();
        });
    }
};

const handleKeydown = (index, event) => {
    // Handle Enter key - emit event for form submission
    if (event.key === 'Enter') {
        if (otpValue.value.length === props.length) {
            emit('enter', event);
        }
        return;
    }
    
    // Handle backspace
    if (event.key === 'Backspace') {
        if (!digits.value[index] && index > 0) {
            // If current field is empty, go to previous and clear it
            digits.value[index - 1] = '';
            nextTick(() => {
                inputRefs.value[index - 1]?.focus();
            });
        } else {
            // Clear current field
            digits.value[index] = '';
        }
    }
    // Handle arrow keys
    else if (event.key === 'ArrowLeft' && index > 0) {
        inputRefs.value[index - 1]?.focus();
    } else if (event.key === 'ArrowRight' && index < props.length - 1) {
        inputRefs.value[index + 1]?.focus();
    }
    // Prevent non-alphanumeric characters
    else if (!/[0-9a-zA-Z]/.test(event.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(event.key)) {
        event.preventDefault();
    }
};

const handlePaste = (event) => {
    event.preventDefault();
    fillFromStart(event.clipboardData?.getData('text') ?? '');
};

const handleFocus = (index) => {
    // Select all text when focusing
    nextTick(() => {
        inputRefs.value[index]?.select();
    });
};

onMounted(() => {
    // Focus first input if all are empty
    if (otpValue.value === '') {
        nextTick(() => {
            inputRefs.value[0]?.focus();
        });
    }
});
</script>

<style scoped>
.otp-input-container input:focus {
    z-index: 10;
    position: relative;
}
</style>

